import * as React from "react"
import {Form, Icon, InputOnChangeData, Search, SearchProps} from "semantic-ui-react"
import {BookerFragFragment, Bookers, CreateBooker} from "../graphql/queries"
import {Booker} from "../model/BookingTypes"
import {GraphQL} from "../graphql"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {isValidEmailAddress} from "../email"
import {WithModalStore} from "../App"
import {Link} from "react-router-dom"

interface BookerSearchProps {
  onBooker: (booker: Booker) => void
  onSearch: (searchTerm: string) => void
  loading: boolean
  searchResults: Array<Booker>
  searchTerm: string
  disabled: boolean
}

function BookerSearch({
  onBooker,
  onSearch,
  loading,
  searchResults,
  searchTerm,
  disabled,
}: BookerSearchProps) {
  return (
    <Search
      placeholder={"Booker"}
      loading={loading}
      onResultSelect={async (e: React.MouseEvent<HTMLDivElement>, data: SearchProps) => {
        // This works around a bug in Search, see the comment in PlaceSuggest.tsx.
        await null
        onBooker(searchResults[data.result.id])
      }}
      onSearchChange={(e: React.MouseEvent<HTMLElement>, data: SearchProps) => {
        if (data.value !== undefined) {
          onSearch(data.value)
        }
      }}
      results={searchResults.map(({id, name, email}, index) => ({
        key: id,
        title: name,
        description: email,
        id: index,
      }))}
      value={searchTerm}
      showNoResults={true}
      minCharacters={2}
      disabled={disabled}
      fluid
    />
  )
}

// Outer component that handles the GraphQL query

interface BookerSearchWithQueryProps {
  onBooker: (booker: Booker) => void
  onSearch: (searchTerm: string) => void
  searchTerm: string
  disabled: boolean
}

function BookerSearchWithQuery(props: BookerSearchWithQueryProps) {
  const {searchTerm} = props
  return (
    <GraphQL.Query
      query={Bookers}
      skip={searchTerm.length < 2}
      variables={{searchTerm}}
      render={({data, loading}) => {
        const Next = (searchResults: Array<Booker>) => (
          <BookerSearch {...props} searchResults={searchResults} loading={loading} />
        )
        return data
          ? handleError(errorUnionToEither(data.bookers), (data) => Next(data.bookers))
          : Next([])
      }}
    />
  )
}

// Outer component with state to hold the search term

interface BookerSearchWithSearchTermProps {
  onBooker: (booker: Booker) => void
  disabled: boolean
}

class BookerSearchWithSearchTerm extends React.Component<
  BookerSearchWithSearchTermProps,
  {searchTerm: string}
> {
  constructor(props: BookerSearchWithSearchTermProps) {
    super(props)
    this.state = {
      searchTerm: "",
    }
  }

  render() {
    return (
      <BookerSearchWithQuery
        onBooker={this.props.onBooker}
        onSearch={(searchTerm) => {
          this.setState({searchTerm})
        }}
        searchTerm={this.state.searchTerm}
        disabled={this.props.disabled}
      />
    )
  }
}

// Outer component that also displays the booker when one is selected

interface OwnProps extends WithModalStore {
  booker: Booker | null
  bookerFormOpen: boolean
  onBooker: (booker: Booker | null) => void
  disabled: boolean
  onBookerFormOpen: ((open: boolean) => void) | null
}

function BookerSearchWithBooker({
  booker,
  onBooker,
  disabled,
  modalStore,
  onBookerFormOpen,
  bookerFormOpen,
}: OwnProps) {
  if (booker !== null) {
    const {name, email, id} = booker
    return (
      <div>
        <Icon
          link={!disabled}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              onBooker(null)
            }
          }}
          name="close"
          color="grey"
        />
        <span style={{display: "inline-block", width: 10}} />
        <Link to={"/profile/" + id}>
          {name} ({email})
        </Link>
      </div>
    )
  } else if (onBookerFormOpen) {
    return (
      <BookerPickerNoBooker
        disabled={disabled}
        onBooker={onBooker}
        modalStore={modalStore}
        onBookerFormOpen={onBookerFormOpen}
        bookerFormOpen={bookerFormOpen}
      />
    )
  } else {
    return <BookerSearchWithSearchTerm onBooker={onBooker} disabled={disabled} />
  }
}

interface BookerPickerNoBookerProps extends WithModalStore {
  disabled: boolean
  onBooker: (booker: BookerFragFragment) => void
  bookerFormOpen: boolean
  onBookerFormOpen: (open: boolean) => void
}

function BookerPickerNoBooker(props: BookerPickerNoBookerProps) {
  const {onBooker, disabled, modalStore, onBookerFormOpen} = props

  if (!props.bookerFormOpen) {
    return (
      <>
        <Form.Field>
          <BookerSearchWithSearchTerm onBooker={onBooker} disabled={disabled} />
        </Form.Field>
        <Form.Button
          compact
          labelPosition="right"
          content="New booker"
          onClick={() => {
            onBookerFormOpen(true)
          }}
          icon="chevron right"
        />
      </>
    )
  } else {
    return (
      <NewBookerForm
        initialState={{
          name: "",
          email: "",
        }}
        onReset={() => {
          onBookerFormOpen(false)
        }}
        onBooker={(booker) => {
          onBooker(booker)
          onBookerFormOpen(false)
        }}
        disabled={false}
        modalStore={modalStore}
      />
    )
  }
}

interface NewBookerFormProps extends WithModalStore {
  initialState: NewBookerFormState
  onBooker: (booker: Booker) => void
  onReset: () => void
  disabled: boolean
}

interface NewBookerFormState {
  name: string
  email: string
}

class NewBookerForm extends React.Component<NewBookerFormProps, NewBookerFormState> {
  constructor(props: NewBookerFormProps) {
    super(props)
    this.state = props.initialState
  }

  render() {
    const {name, email} = this.state
    const {disabled, modalStore} = this.props
    const emailValid = isValidEmailAddress(email)
    const dataValid = name && email && emailValid

    return (
      <>
        <Form.Group>
          <Form.Input
            required
            label="Name"
            placeholder="Name"
            value={name}
            disabled={disabled}
            onChange={(e: any, field: InputOnChangeData) => {
              this.setState({name: field.value})
            }}
          />
          <Form.Input
            required
            type="email"
            label="Email"
            placeholder="Email"
            error={email !== "" && !emailValid}
            value={email}
            disabled={disabled}
            onChange={(e: any, field: InputOnChangeData) => {
              this.setState({email: field.value})
            }}
          />
        </Form.Group>
        <Form.Group>
          <Form.Button
            compact
            children="Save"
            onClick={() => {
              modalStore.handleActionThrow(async () => {
                await modalStore.applyModal({
                  pending: ["Creating booker", "Creating booker..."],
                  error: (m) => ["Error creating booker", m],
                  action: async () => {
                    const result = await GraphQL.mutate(CreateBooker, {
                      name: this.state.name,
                      email: this.state.email,
                      matchByEmail: true,
                    })

                    return errorUnionToEither(result.createBooker).map((booker) => {
                      this.props.onBooker(booker)
                    })
                  },
                })
              })
            }}
            disabled={disabled || !dataValid}
          />
          <Form.Button
            compact
            children="Reset"
            onClick={() => {
              this.props.onReset()
            }}
          />
        </Form.Group>
      </>
    )
  }
}

export {BookerSearchWithBooker as BookerPicker}



// WEBPACK FOOTER //
// ./src/components/BookerPicker.tsx